// modified by Katarzyna Krasnowska for SCWAD demo

import React from 'react';
import styled from 'styled-components';
import { BrowserRouter as Router, Route, Redirect, Switch } from 'react-router-dom';
import { ThemeProvider } from '@allenai/varnish/theme';
import { DefaultLayoutProvider } from '@allenai/varnish/layout';
import {
  Content,
  HeaderColumns,
  Layout,
} from '@allenai/varnish/components';
import { ScrollToTopOnPageChange} from '@allenai/varnish/components/ScrollToTopOnPageChange';
import { Header } from './components/scwad/Header'
import { Footer } from './components/scwad/Footer'

import zilLogo from './components/scwad/logoZIL.svg';
import ipiLogo from './components/scwad/logoIPI.svg';
import clarinLogo from './components/scwad/logoCLARIN.svg'
import { API_ROOT } from './api-config';
import Menu from './components/Menu';
import { modelComponents, modelRedirects } from './models'
import WaitingForPermalink from './components/WaitingForPermalink';

import './css/App.css';
import './css/fonts.css';
import './css/icons.css';
import './css/Accordion.css';
import './css/hierplane-overrides.css';
import './css/visualization-types.css';


/*******************************************************************************
  <App /> Container
*******************************************************************************/

const DEFAULT_PATH = "/combo-eng";

/*
The App is just a react-router wrapped around the Demo component.
The design is a bit convoluted so that the same code can run
the overall demo frontend or an individual model demo.
Here's how it accomplishes that:

If you request `/` (corresponding to https://demo.allennlp.org), you will get
redirected to the default model (here, /reading-comprehension).

The overall front-end service serves all routes that look like `/<model_name>`.
When you request `/<model_name>`, you will be served the <Demo> component,
which shows some chrome, a menu, and a <SingleTaskFrame> that's an iframe with src
`/task/<model_name>`, which the k8s ingress controller will direct to the machine
serving that specific demo. That machine may be doing *anything*, as long as it serves
its demo front-end at `/task/<model_name>`.

In particular, that machine may be also running this code,
for which the route `/task/<model_name>` serves the <SingleTaskDemo> component,
which delegates to the particular ModelComponent specified in `demo/src/models.js`.
*/
const App = () => (
  <ThemeProvider>
    <Router>
      <DefaultLayoutProvider layoutVariant="app">
        <ScrollToTopOnPageChange />
        <Switch>
          <Route exact path="/" render={() => (
            <Redirect to={DEFAULT_PATH}/>
          )}/>
          <Route path="/:model/:slug?" component={Demo}/>
        </Switch>
      </DefaultLayoutProvider>
    </Router>
  </ThemeProvider>
)

// This is the top-level demo component.
// It handles the chrome for header and menus,
// and it renders the specific task in an iframe.
const Demo = (props) => {
  const { model, slug } = props.match.params
  const redirectedModel = modelRedirects[model] || model

  /* TODO?: remove the top “Allen Institute...” banner? create a “ZIL” banner instead? */
  return (
    <Layout bgcolor="white">
      <Header>
        <HeaderColumnsWithSpace gridTemplateColumns="auto auto 1fr">
          <a href="https://ipipan.waw.pl" target="_blank" rel="noopener noreferrer">
            <LogoIPI height="50px"
              alt="Instytut Podstaw Informatyki PAN"
            />
          </a>
          <a href="https://clarin.biz" target="_blank" rel="noopener noreferrer">
            <LogoClarin height="50px"
              alt="CLARIN-PL-Biz"
            />
          </a>
        </HeaderColumnsWithSpace>
      </Header>
      <Layout>
        <Menu redirectedModel={redirectedModel} />
        <Layout>
          <FullSizeContent>
            <SingleTaskDemo model={redirectedModel} slug={slug} />
          </FullSizeContent>
          <Footer />
        </Layout>
      </Layout>
    </Layout>
  );
}

const FullSizeContent = styled(Content)`
    padding: 0;
`;

const LogoZIL = styled.img.attrs({
  src: zilLogo
})``;

const LogoIPI = styled.img.attrs({
  src: ipiLogo
})``;

const LogoClarin = styled.img.attrs({
  src: clarinLogo
})``;

const HeaderColumnsWithSpace = styled(HeaderColumns)`
    padding: 6.5px 0;
`;

class SingleTaskDemo extends React.Component {
  constructor(props) {
    super(props);

    // React router supplies us with a model name and (possibly) a slug.
    const { model, slug } = props;

    this.state = {
      slug,
      selectedModel: model,
      requestData: null,
      responseData: null
    }
  }

  // We also need to update the state whenever we receive new props from React router.
  componentDidUpdate() {
    const { model, slug } = this.props;
      if (model !== this.state.selectedModel || slug !== this.state.slug) {
        const isModelChange = model !== this.state.selectedModel;
        const responseData = (
            isModelChange
                ? null
                : this.state.responseData
        );
        const requestData = (
            isModelChange
                ? null
                : this.state.requestData
        );
        this.setState({ selectedModel: model, slug, responseData, requestData });
    }
  }

  // After the component mounts, we check if we need to fetch the data
  // for a permalink.
  componentDidMount() {
    const { slug, responseData } = this.state;
    const { model } = this.props;

    // If this is a permalink and we don't yet have the data for it...
    if (slug && !responseData) {
      // Make an ajax call to get the permadata,
      // and then use it to update the state.
      fetch(`${API_ROOT}/permadata/${model}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({"slug": slug})
      }).then(function(response) {
        return response.json();
      }).then((json) => {
        const { requestData, responseData } = json;
        this.setState({requestData, responseData});
      }).catch((error) => {
        this.setState({outputState: "error"});
        console.error(error);
      });
    }
  }

  render() {
    const { slug, selectedModel, requestData, responseData } = this.state;
    const updateData = (requestData, responseData) => this.setState({requestData, responseData})

    if (slug && !responseData) {
      // We're still waiting for permalink data, so just return the placeholder component.
      return (<WaitingForPermalink/>)
    } else if (modelComponents[selectedModel]) {
        // This is a model we know the component for, so render it.
        return React.createElement(modelComponents[selectedModel], {requestData, responseData, selectedModel, updateData})
    }
  }
}

export default App;
